import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Fragment } from 'react';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  PartyNotificationSettings,
  StorageSettings,
  AssetVisibility,
  ActivationNotification,
} from 'src/containers/SoftwareEscrow/Technical';
import { DisruptionNotificationEnum, DisruptionNotificationGracePeriodEnum } from 'src/graphql/schema';
import { useNotificationsSuggestChanges } from 'src/context/suggest-changes';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { Button } from '@/components/ui/button';

import { getDisruptionNotificationGracePeriod } from '../lib';

const NotificationSettings = () => {
  const {
    softwareEscrow: {
      depositorActivityNotification,
      depositorDisruptionNotification,
      depositorDisruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    state,
    updateDepositorDepositNotification,
    updateDepositorDisruptNotification,
    updateDepositorDisruptNotificationGrace,
  } = useNotificationsSuggestChanges();

  const onDisruptionNotificationChange = (disruptionNotification: DisruptionNotificationEnum) => {
    const gracePeriodMap = {
      [DisruptionNotificationEnum.NoNotifications]: depositorDisruptionNotificationGracePeriod,
      [DisruptionNotificationEnum.SendNotification]:
        depositorDisruptionNotificationGracePeriod ?? DisruptionNotificationGracePeriodEnum.Immediate,
    };

    updateDepositorDisruptNotification(disruptionNotification, gracePeriodMap[disruptionNotification]);
  };

  const disruptionNotificationGracePeriod = getDisruptionNotificationGracePeriod({
    disruptionNotification: state.depositorDisruptionNotification ?? depositorDisruptionNotification,
    newDisruptionNotificationGracePeriod: state.depositorDisruptionNotificationGracePeriod,
    oldDisruptionNotificationGracePeriod: depositorDisruptionNotificationGracePeriod,
  });

  return (
    <PartyNotificationSettings
      settings={{
        activityNotification: state.depositorActivityNotification ?? depositorActivityNotification,
        disruptionNotification: state.depositorDisruptionNotification ?? depositorDisruptionNotification,
        disruptionNotificationGracePeriod,
      }}
      onActivityNotificationChange={updateDepositorDepositNotification}
      onDisruptionNotificationChange={onDisruptionNotificationChange}
      onGracePeriodChange={updateDepositorDisruptNotificationGrace}
    />
  );
};

const ActivationNotificationSettings = () => {
  const {
    state: { activationNotification },
    updateActivationNotification,
  } = useNotificationsSuggestChanges();

  return (
    <div className="w-[580px] max-w-full mx-auto">
      <ActivationNotification
        activationNotification={Boolean(activationNotification)}
        onActivationNotificationChange={updateActivationNotification}
      />
    </div>
  );
};

const AssetVisibilitySettings = () => {
  const {
    softwareEscrow: {
      beneficiarySharedInformation,
      depositorActivityNotification,
      depositorDisruptionNotification,
      depositorDisruptionNotificationGracePeriod,
      currentCompanyRole,
    },
  } = useSoftwareEscrow();
  const { state, updateDepositDisplay } = useNotificationsSuggestChanges();

  const onAssetVisibilityChange = async (beneficiarySharedInformation: boolean | null) => {
    if (beneficiarySharedInformation === null) return;

    updateDepositDisplay({
      ...(!beneficiarySharedInformation
        ? {
            beneficiarySharedInformation: false,
            depositorActivityNotification,
            depositorDisruptionNotification,
            depositorDisruptionNotificationGracePeriod,
          }
        : {
            beneficiarySharedInformation: true,
          }),
    });
  };

  return (
    <div className="w-[580px] max-w-full mx-auto">
      <AssetVisibility
        onAssetVisibilityChange={onAssetVisibilityChange}
        assetVisibility={state.beneficiarySharedInformation ?? beneficiarySharedInformation}
        party={currentCompanyRole!}
      />
    </div>
  );
};

const StorageDepositorSettings = () => {
  const { state, updateStorageRegion, updateAssetVersionsLimit } = useNotificationsSuggestChanges();

  const onAssetVersionsLimitChange = (value: string) =>
    updateAssetVersionsLimit(value === 'full' ? null : Number(value));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      <StorageSettings
        storageRegion={state.storageRegion ?? ''}
        assetVersionsLimit={state.assetVersionsLimit ?? undefined}
        onStorageRegionChange={updateStorageRegion}
        onAssetVersionsLimitChange={onAssetVersionsLimitChange}
      />
    </div>
  );
};

const settingsStepsMap = {
  notification: {
    component: NotificationSettings,
    title: 'Notification settings for the depositor',
    description: 'Manage the information and the notifications you’ll receive.',
  },
  activation: {
    component: ActivationNotificationSettings,
    title: 'Activation notifications for beneficiaries',
    description:
      'Control whether the beneficiary gets notified when this escrow becomes active. No other notifications will automatically be shared with the beneficiary of a bipartite escrow.',
  },
  asset: {
    component: AssetVisibilitySettings,
    title: 'Asset visibility for beneficiaries',
    description:
      'Control whether the beneficiary can view details about deposited assets, including  file names, deposit dates, and connection status.',
  },
  storage: {
    component: StorageDepositorSettings,
    title: 'Storage settings',
    description: 'Configure the settings related to the deposited assets, and their storage in escrow.',
  },
} as const;

const Navigation = () => {
  const { steps, currentStep, setCurrentStep } = useNotificationsSuggestChanges();

  const onNextStep = () => setCurrentStep((prevStep) => prevStep + 1);

  const onPreviousStep = () => setCurrentStep((prevStep) => prevStep - 1);

  if (steps.length === 1) return null;

  return (
    <Fragment>
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-[50%] -translate-y-1/2 left-10 size-8 rounded-full border-border"
        onClick={onPreviousStep}
        disabled={currentStep === 0}
      >
        <ArrowLeft />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-[50%] -translate-y-1/2 right-10 size-8 rounded-full border-border"
        onClick={onNextStep}
        disabled={currentStep === steps.length - 1}
      >
        <ArrowRight />
      </Button>
    </Fragment>
  );
};

const DepositorCreator = () => {
  const { steps, currentStep } = useNotificationsSuggestChanges();

  const {
    component: Component,
    title,
    description,
  } = settingsStepsMap[steps[currentStep ?? 0] as keyof typeof settingsStepsMap];

  return (
    <SoftwareOnboardingMain
      subtitle="Request changes to technical information:"
      title={title}
      description={description}
      classes={{
        subtitle: 'font-medium',
      }}
      className={steps.length > 1 ? 'px-10.5' : ''}
    >
      <Navigation />
      <div className="container mx-auto grow-1">
        <Component />
      </div>
    </SoftwareOnboardingMain>
  );
};

export default DepositorCreator;
